/**
 * SupplierInvoiceFormModal — tedarikçi faturası oluşturma.
 *
 * İki mod:
 * 1) Serbest: tedarikçi + satırlar elle girilir.
 * 2) GR'den (prefill): bir mal girişinin satırları önceden dolu gelir, her
 *    satır `goods_receipt_line` taşır → fatura POSTED olunca GR/IR mahsubu
 *    (İrsaliye borcu ters'lenir, INVOICE düşer) çift sayım olmadan çalışır.
 *
 * Backend: POST /api/v1/supplier-invoices/ (custom create, lines işler)
 *   { supplier, invoice_number, invoice_date, due_date?, notes?,
 *     lines: [{description, quantity, unit_price_minor, tax_rate_percent?,
 *              ingredient?, goods_receipt_line?}] }
 */
import { useEffect, useState } from "react";
import {
  ActionIcon, Badge, Button, FileInput, Group, Modal, NumberInput, Stack,
  Table, Text, TextInput,
} from "@mantine/core";
import { IconPaperclip, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../../shared/api";
import { MoneyDisplay } from "../../../shared/ui";
import { SupplierPickerModal } from "./SupplierPickerModal";

/** GR'den prefill edilen fatura satırı. */
export interface InvoicePrefillLine {
  grLineId: string;
  itemName: string;
  quantity: number | string;
  unitPriceMinor: number;
}

interface DraftLine {
  key: string;
  description: string;
  quantity: number | string;
  unitPriceMinor: number | string; // kuruş
  taxRatePercent: number | string;
  /** GR'den geldiyse mahsup için. */
  grLineId?: string;
  /** GR teslim fiyatı (3-way fark rozeti için referans). */
  grPriceMinor?: number;
}

interface Props {
  opened: boolean;
  onClose: () => void;
  prefill?: {
    supplierId: string;
    supplierName: string;
    goodsReceiptId: string;
    lines: InvoicePrefillLine[];
  } | null;
}

export function SupplierInvoiceFormModal({ opened, onClose, prefill }: Props) {
  const qc = useQueryClient();
  const isPrefilled = !!prefill;

  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [supplierName, setSupplierName] = useState("");
  const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(() => isoToday());
  const [dueDate, setDueDate] = useState("");
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Toplu KDV (UX kolaylığı): tek değer → tüm satırlara uygula.
  const [bulkTax, setBulkTax] = useState<number | string>(20);

  useEffect(() => {
    if (!opened) return;
    if (prefill) {
      setSupplierId(prefill.supplierId);
      setSupplierName(prefill.supplierName);
      setLines(
        prefill.lines.map((l) => ({
          key: `grl-${l.grLineId}`,
          description: l.itemName,
          quantity: l.quantity,
          unitPriceMinor: l.unitPriceMinor,
          taxRatePercent: 0,
          grLineId: l.grLineId,
          grPriceMinor: l.unitPriceMinor,
        })),
      );
    }
  }, [opened, prefill]);

  function addLine() {
    setLines((p) => [
      ...p,
      { key: `new-${p.length}-${Math.round(Number(invoiceDate.replace(/\D/g, "")) % 100000)}`, description: "", quantity: 1, unitPriceMinor: 0, taxRatePercent: 0 },
    ]);
  }
  function updateLine(key: string, patch: Partial<DraftLine>) {
    setLines((p) => p.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }
  function removeLine(key: string) {
    setLines((p) => p.filter((l) => l.key !== key));
  }
  function applyBulkTax() {
    const v = Number(bulkTax) || 0;
    setLines((p) => p.map((l) => ({ ...l, taxRatePercent: v })));
  }
  function reset() {
    setSupplierId(null); setSupplierName(""); setInvoiceNumber("");
    setInvoiceDate(isoToday()); setDueDate(""); setLines([]); setAttachment(null);
  }

  const subtotal = lines.reduce(
    (s, l) => s + Math.round(Number(l.quantity) * Number(l.unitPriceMinor)), 0,
  );
  const taxTotal = lines.reduce(
    (s, l) => s + Math.round(Number(l.quantity) * Number(l.unitPriceMinor) * Number(l.taxRatePercent) / 100), 0,
  );
  const grandTotal = subtotal + taxTotal;

  // GR'den fatura kesiliyorsa (prefill): mal kabulünde OLMAYAN serbest kalemler
  // (grLineId yok) three-way match'i bozar → uyarı (engelleme değil; nakliye/
  // hizmet gibi gerçek ek kalem olabilir).
  const unmatchedLines = isPrefilled ? lines.filter((l) => !l.grLineId) : [];
  // PPV (fiyat farkı): mahsup edilen irsaliye net toplamı vs fatura net (KDV
  // hariç). KDV "fark" değildir; ayrı gösterilir. İrsaliye net = GR'den gelen
  // satırların grPriceMinor × miktar toplamı.
  const irsaliyeNetMinor = isPrefilled
    ? lines.reduce(
        (s, l) => s + (l.grLineId && l.grPriceMinor
          ? Math.round(Number(l.quantity) * l.grPriceMinor) : 0), 0,
      )
    : 0;
  const ppvMinor = subtotal - irsaliyeNetMinor; // + = fatura irsaliyeden pahalı

  async function submit() {
    if (!supplierId) { notifications.show({ message: "Tedarikçi seçin", color: "orange" }); return; }
    if (!invoiceNumber.trim()) { notifications.show({ message: "Fatura no girin", color: "orange" }); return; }
    const active = lines.filter((l) => Number(l.quantity) > 0);
    if (active.length === 0) { notifications.show({ message: "En az bir satır girin", color: "orange" }); return; }
    const linesPayload = active.map((l) => ({
      description: l.description,
      quantity: Number(l.quantity),
      unit_price_minor: Math.round(Number(l.unitPriceMinor)),
      tax_rate_percent: Number(l.taxRatePercent),
      ...(l.grLineId ? { goods_receipt_line: l.grLineId } : {}),
    }));
    setSubmitting(true);
    try {
      if (attachment) {
        // Dosya eki var → multipart/form-data (lines JSON string olarak).
        const fd = new FormData();
        fd.append("supplier", supplierId);
        fd.append("invoice_number", invoiceNumber.trim());
        fd.append("invoice_date", invoiceDate);
        if (dueDate) fd.append("due_date", dueDate);
        fd.append("lines", JSON.stringify(linesPayload));
        fd.append("attachment", attachment);
        await apiClient.post("/api/v1/supplier-invoices/", fd);
      } else {
        await apiClient.post("/api/v1/supplier-invoices/", {
          supplier: supplierId,
          invoice_number: invoiceNumber.trim(),
          invoice_date: invoiceDate,
          ...(dueDate ? { due_date: dueDate } : {}),
          lines: linesPayload,
        });
      }
      qc.invalidateQueries({ queryKey: ["supplier-invoices"] });
      qc.invalidateQueries({ queryKey: ["supplier-ledger"] });
      qc.invalidateQueries({ queryKey: ["supplier-balance"] });
      // Mal girişinden fatura kesilince o GR "Faturalandı" olur; GR listesinin
      // is_invoiced/status'u değişir → listeyi de tazele (aksi halde panel
      // kapanınca liste eski kalır, ancak tab değişince görünürdü).
      qc.invalidateQueries({ queryKey: ["goods-receipts"] });
      notifications.show({ message: "Fatura oluşturuldu (taslak) — 'İşle' ile cariye işlenir", color: "green" });
      reset(); onClose();
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Fatura oluşturulamadı", color: "red" });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={isPrefilled ? "Mal Girişinden Fatura Oluştur" : "Yeni Tedarikçi Faturası"}
        size="xl"
      >
        <Stack gap="md">
          <Group grow align="flex-end">
            <Stack gap={4}>
              <Text size="sm" fw={500}>Tedarikçi <Text span c="red">*</Text></Text>
              <Button
                variant="default"
                justify="space-between"
                rightSection={isPrefilled ? null : <IconSearch size={16} />}
                onClick={() => !isPrefilled && setSupplierPickerOpen(true)}
                disabled={isPrefilled}
              >
                {supplierId ? supplierName : "Tedarikçi seç"}
              </Button>
            </Stack>
            <TextInput
              label="Fatura No"
              required
              placeholder="Tedarikçinin fatura numarası"
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.currentTarget.value)}
            />
          </Group>
          <Group grow>
            <TextInput
              label="Fatura Tarihi"
              type="date"
              required
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.currentTarget.value)}
            />
            <TextInput
              label="Vade Tarihi"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.currentTarget.value)}
            />
          </Group>
          <FileInput
            label="Fatura Belgesi (PDF/görsel, ≤10MB)"
            placeholder="Dosya seç (opsiyonel)"
            leftSection={<IconPaperclip size={16} />}
            accept="application/pdf,image/jpeg,image/png,image/webp"
            value={attachment}
            onChange={setAttachment}
            clearable
            maw={420}
          />

          {isPrefilled && (
            <Text size="xs" c="dimmed">
              Satırlar mal girişinden dolduruldu. Birim fiyat teslim fiyatından
              farklıysa rozet gösterilir (3-way kontrol). Fatura "İşle"nince
              irsaliye borcu fatura ile mahsuplaşır (çift kayıt olmaz).
            </Text>
          )}

          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Açıklama</Table.Th>
                <Table.Th w={90}>Miktar</Table.Th>
                <Table.Th w={140}>Birim Fiyat</Table.Th>
                <Table.Th w={80}>KDV %</Table.Th>
                <Table.Th ta="right">Satır Tutarı</Table.Th>
                <Table.Th w={44} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {lines.map((l) => {
                const lineNet = Math.round(Number(l.quantity) * Number(l.unitPriceMinor));
                const variance = priceVariancePct(Number(l.unitPriceMinor), l.grPriceMinor);
                return (
                  <Table.Tr key={l.key}>
                    <Table.Td>
                      <TextInput
                        size="xs"
                        value={l.description}
                        onChange={(e) => updateLine(l.key, { description: e.currentTarget.value })}
                        placeholder="Kalem açıklaması"
                      />
                      {/* GR'den fatura kesilirken mal kabulünde olmayan serbest
                          kalem (three-way match uyarısı). */}
                      {isPrefilled && !l.grLineId && (
                        <Badge size="xs" mt={2} variant="light" color="orange">
                          ⚠ Mal kabulünde yok
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <NumberInput size="xs" value={l.quantity} min={0} decimalScale={3}
                        onChange={(v) => updateLine(l.key, { quantity: v })} />
                    </Table.Td>
                    <Table.Td>
                      <NumberInput
                        size="xs"
                        value={Number(l.unitPriceMinor) / 100}
                        min={0}
                        decimalScale={2}
                        fixedDecimalScale
                        suffix=" ₺"
                        onChange={(v) => updateLine(l.key, { unitPriceMinor: Math.round(Number(v) * 100) })}
                      />
                      {variance && (
                        <Badge size="xs" mt={2} variant="light" color={variance.up ? "red" : "green"}>
                          {variance.up ? "▲ +" : "▼ "}{variance.pct.toFixed(1)}% (teslim {(l.grPriceMinor! / 100).toFixed(2)}₺)
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <NumberInput size="xs" value={l.taxRatePercent} min={0} max={100}
                        onChange={(v) => updateLine(l.key, { taxRatePercent: v })} />
                    </Table.Td>
                    <Table.Td ta="right" fw={600}>
                      <MoneyDisplay minor={lineNet} />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon variant="subtle" color="red" onClick={() => removeLine(l.key)} aria-label="Sil">
                        <IconTrash size={15} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {lines.length === 0 && (
                <Table.Tr><Table.Td colSpan={6} ta="center" c="dimmed">Satır yok</Table.Td></Table.Tr>
              )}
            </Table.Tbody>
          </Table>

          {/* Üst satır: toplu KDV (sol) + toplamlar (sağ). */}
          <Group justify="space-between">
            <Group gap="xs" align="flex-end">
              {/* Toplu KDV — tek değer gir, "Hepsine Uygula" ile tüm satırlara yaz. */}
              <NumberInput
                size="xs" label="KDV %" w={90} min={0} max={100}
                value={bulkTax} onChange={setBulkTax}
              />
              <Button
                variant="default" size="xs"
                onClick={applyBulkTax}
                disabled={lines.length === 0}
              >
                KDV'yi Hepsine Uygula
              </Button>
            </Group>
            <Stack gap={2} align="flex-end">
              {/* GR'den fatura kesiliyorsa fatura≠irsaliye farkını ayrıştır:
                  İrsaliye(net) + Fiyat farkı(PPV) = Fatura net; üstüne KDV. */}
              {isPrefilled && (
                <>
                  <Text size="xs" c="dimmed">
                    İrsaliye (net): <MoneyDisplay minor={irsaliyeNetMinor} />
                  </Text>
                  {ppvMinor !== 0 && (
                    <Text size="xs" c={ppvMinor > 0 ? "red" : "green"}>
                      Fiyat farkı (PPV): {ppvMinor > 0 ? "+" : ""}
                      <MoneyDisplay minor={ppvMinor} />
                    </Text>
                  )}
                </>
              )}
              <Text size="xs" c="dimmed">Ara Toplam (net): <MoneyDisplay minor={subtotal} /></Text>
              <Text size="xs" c="dimmed">KDV: <MoneyDisplay minor={taxTotal} /></Text>
              <Text fw={700}>Genel Toplam: <MoneyDisplay minor={grandTotal} fw={700} /></Text>
            </Stack>
          </Group>

          {/* Alt satır: Satır Ekle (KDV üstte kaldı). */}
          <Group>
            <Button variant="light" size="xs" leftSection={<IconPlus size={14} />} onClick={addLine}>
              Satır Ekle
            </Button>
          </Group>

          {/* Three-way match toplu uyarı — GR'de olmayan kalem(ler) var. */}
          {unmatchedLines.length > 0 && (
            <Text size="xs" c="orange">
              ⚠ {unmatchedLines.length} kalem mal kabulünde yok (sonradan eklendi).
              Nakliye/hizmet gibi gerçek ek değilse kontrol edin — fatura mal
              kabulüyle birebir eşleşmiyor.
            </Text>
          )}

          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>İptal</Button>
            <Button leftSection={<IconPlus size={16} />} onClick={submit} loading={submitting}>
              Fatura Oluştur
            </Button>
          </Group>
        </Stack>
      </Modal>

      <SupplierPickerModal
        opened={supplierPickerOpen}
        onClose={() => setSupplierPickerOpen(false)}
        onSelect={(s) => { setSupplierId(s.id); setSupplierName(s.name); }}
      />
    </>
  );
}

function isoToday(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

function priceVariancePct(invoicePriceMinor: number, grPriceMinor?: number) {
  if (!grPriceMinor || grPriceMinor <= 0) return null;
  const pct = ((invoicePriceMinor - grPriceMinor) / grPriceMinor) * 100;
  if (Math.abs(pct) < 0.5) return null;
  return { pct, up: pct > 0 };
}
